import { PosModel } from '@/types'

/**
 * Finds and applies offers to a cart on the till (POS.md §13.1, O3: identification is a frontend job; the server stores
 * what was sold and never re-resolves). Offers come from the device database (`offers`, pulled for this branch).
 *
 * Rules (§13.1):
 * - An offer fires from the cart's own quantities; items made cheaper by one offer never trigger another.
 * - Triggers are counted per product: "buy 2 A and 1 B" needs both.
 * - Best deal wins per product; discounts never stack. A line with a manual discount gets no offer.
 * - When only some units qualify, the line is split: the discounted part carries `offer_id`, the rest is full price.
 *
 * Types: `single_product`, `buy_x_get_y` (free item = 100 %), `volume_discount`, `combo_fixed_price` (fixed set price).
 */

export interface PosOffer {
  _id: string
  name?: string
  type: 'single_product' | 'combo_fixed_price' | 'buy_x_get_y' | 'volume_discount'
  /** Shape depends on `type` (server `Offer.offer_rules`); read loosely and checked where used. */
  offer_rules: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
  status?: string
  /** Only `pos` offers apply at the till; devices may still hold `both` / `online` rows synced earlier. */
  applies_to?: string
  starts_at?: string | null
  ends_at?: string | null
  deleted_at?: string | null
}

type Tier = { min_quantity: number; max_quantity?: number | null; discount_type: string; discount_value: number }

type Ref = { product_variation_id?: string; product_id?: string; category_id?: string; brand_id?: string }

const round = (n: number) => Math.round(n * 100) / 100

const matches = (ref: Ref, product: PosModel.Product) =>
  (ref.product_variation_id && (String(ref.product_variation_id) === product._id || String(ref.product_variation_id) === product.server_id)) ||
  (ref.product_id && String(ref.product_id) === String(product.product_id)) ||
  (ref.category_id && String(ref.category_id) === product.category_id) ||
  (ref.brand_id && String(ref.brand_id) === String((product as { brand_id?: string }).brand_id ?? ''))

const unitOff = (price: number, type: string, value: number) =>
  Math.min(price, Math.max(0, type === 'percentage' ? (price * Math.min(value, 100)) / 100 : value))

/** Offers that apply now: POS-only, active, not deleted, inside their window. */
export const activeOffers = (offers: PosOffer[], now = new Date()) =>
  offers.filter(
    (o) =>
      o.applies_to === 'pos' &&
      o.status === 'active' &&
      !o.deleted_at &&
      (!o.starts_at || new Date(o.starts_at) <= now) &&
      (!o.ends_at || new Date(o.ends_at) >= now)
  )

/** A candidate discount: `units` of variation `variationId` at `unitDiscount` off each, from `offerId`. */
interface Candidate {
  variationId: string
  units: number
  unitDiscount: number
  offerId: string
}

const candidatesFor = (offer: PosOffer, lines: PosModel.CartLine[]): Candidate[] => {
  const rule = offer.offer_rules ?? {}
  const qtyOf = (ref: Ref) => lines.filter((l) => matches(ref, l.product)).reduce((s, l) => s + l.quantity, 0)
  const out: Candidate[] = []

  if (offer.type === 'single_product') {
    for (const l of lines.filter((line) => matches(rule, line.product))) {
      out.push({ variationId: l.product._id, units: l.quantity, unitDiscount: unitOff(l.product.price, rule.discount_type, rule.discount_value), offerId: offer._id })
    }
  } else if (offer.type === 'buy_x_get_y') {
    const buys: (Ref & { quantity: number })[] = rule.buy_items ?? []
    if (buys.length === 0) return out
    let times = Math.min(...buys.map((b) => Math.floor(qtyOf(b) / Math.max(b.quantity, 1))))
    if (rule.max_applications_per_order) times = Math.min(times, rule.max_applications_per_order)
    if (times < 1) return out
    for (const get of rule.get_items ?? []) {
      const line = lines.find((l) => l.product._id === String(get.product_variation_id))
      if (!line) continue
      out.push({ variationId: line.product._id, units: times * get.quantity, unitDiscount: unitOff(line.product.price, get.discount_type, get.discount_value), offerId: offer._id })
    }
  } else if (offer.type === 'volume_discount') {
    const matching = lines.filter((l) => matches(rule, l.product))
    const volume = matching.reduce((s, l) => s + l.quantity, 0)
    const tier = [...((rule.tiers ?? []) as Tier[])]
      .sort((a, b) => b.min_quantity - a.min_quantity)
      .find((t) => volume >= t.min_quantity && (!t.max_quantity || volume <= t.max_quantity))
    if (!tier) return out
    for (const l of matching) {
      out.push({ variationId: l.product._id, units: l.quantity, unitDiscount: unitOff(l.product.price, tier.discount_type, tier.discount_value), offerId: offer._id })
    }
  } else if (offer.type === 'combo_fixed_price') {
    const items: { product_variation_id: string; quantity: number }[] = rule.items ?? []
    const members = items.map((i) => ({ ...i, line: lines.find((l) => l.product._id === String(i.product_variation_id)) }))
    if (members.length === 0 || members.some((m) => !m.line)) return out
    const sets = Math.min(...members.map((m) => Math.floor(m.line!.quantity / Math.max(m.quantity, 1))))
    const regular = members.reduce((s, m) => s + m.line!.product.price * m.quantity, 0)
    if (sets < 1 || regular <= rule.price) return out
    // The set's saving is spread over its members by value.
    const share = 1 - rule.price / regular
    for (const m of members) {
      out.push({ variationId: m.line!.product._id, units: sets * m.quantity, unitDiscount: m.line!.product.price * share, offerId: offer._id })
    }
  }
  return out
}

/** A reward the cart has earned but does not hold yet: `missing` units of `variationId` (A8). */
export interface PendingReward {
  offerId: string
  offerName?: string
  variationId: string
  missing: number
}

/**
 * Buy-X-get-Y rewards whose condition is met while the reward item is not (or not fully) in the cart, so the cashier
 * can offer it. Competing offers are still settled by "best deal wins" in `applyOffers`.
 */
export const pendingRewards = (lines: PosModel.CartLine[], offers: PosOffer[]): PendingReward[] => {
  const qtyOf = (ref: Ref) => lines.filter((l) => matches(ref, l.product)).reduce((s, l) => s + l.quantity, 0)
  const out: PendingReward[] = []
  for (const offer of offers.filter((o) => o.type === 'buy_x_get_y')) {
    const rule = offer.offer_rules ?? {}
    const buys: (Ref & { quantity: number })[] = rule.buy_items ?? []
    if (buys.length === 0) continue
    let times = Math.min(...buys.map((b) => Math.floor(qtyOf(b) / Math.max(b.quantity, 1))))
    if (rule.max_applications_per_order) times = Math.min(times, rule.max_applications_per_order)
    if (times < 1) continue
    for (const get of rule.get_items ?? []) {
      const id = String(get.product_variation_id)
      const held = lines.filter((l) => l.product._id === id).reduce((s, l) => s + l.quantity, 0)
      const missing = times * get.quantity - held
      if (missing > 0) out.push({ offerId: offer._id, offerName: offer.name, variationId: id, missing })
    }
  }
  return out
}

/** The cart with offers applied: best deal per product, lines split where only some units qualify. */
export const applyOffers = (lines: PosModel.CartLine[], offers: PosOffer[]): PosModel.CartLine[] => {
  if (offers.length === 0) return lines
  const eligible = lines.filter((l) => !l.discount && !l.offer_id)
  const best = new Map<string, Candidate>()
  for (const offer of offers) {
    for (const c of candidatesFor(offer, eligible)) {
      const line = eligible.find((l) => l.product._id === c.variationId)
      if (!line || c.unitDiscount <= 0) continue
      const value = c.unitDiscount * Math.min(c.units, line.quantity)
      const current = best.get(c.variationId)
      if (!current || value > current.unitDiscount * Math.min(current.units, line.quantity)) best.set(c.variationId, c)
    }
  }
  if (best.size === 0) return lines

  return lines.flatMap((line) => {
    const c = !line.discount && !line.offer_id ? best.get(line.product._id) : undefined
    if (!c) return [line]
    const units = Math.min(c.units, line.quantity)
    const discounted: PosModel.CartLine = {
      ...line,
      quantity: units,
      discount: { type: 'fixed', value: round(c.unitDiscount * units), reason: 'Offer' },
      offer_id: c.offerId,
    }
    return units < line.quantity ? [discounted, { ...line, quantity: line.quantity - units }] : [discounted]
  })
}
